/**
 * CoverThumb — square album-art thumbnail for map lists.
 *
 * Shows the cover image when `src` is provided (and loads), otherwise a themed
 * gradient placeholder with the map's first letter. Shared by every editor's
 * "Open Beatmap" dialog so the list rows look identical.
 */

import React, { useState } from 'react';

/** Deterministic hue (0–359) from a string, so each map gets its own stable
 *  placeholder colour across reloads. */
function hashHue(s: string): number {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return h % 360;
}

export default function CoverThumb({
  src, name, size = 56,
}: {
  src?: string | null;
  name?: string;
  size?: number;
}) {
  const [failed, setFailed] = useState(false);
  const clean = name?.trim() || '';
  const letter = (clean[0] || '♪').toUpperCase();
  const showImg = !!src && !failed;

  // Name-derived two-tone gradient → a distinct, album-art-like placeholder per
  // map (instead of every tile sharing the theme colour).
  const hue = hashHue(clean || 'synapse');
  const placeholderBg = `linear-gradient(135deg, oklch(0.58 0.16 ${hue}), oklch(0.34 0.13 ${(hue + 48) % 360}))`;

  return (
    <div
      style={{
        width: size,
        height: size,
        flexShrink: 0,
        borderRadius: 8,
        overflow: 'hidden',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: placeholderBg,
        border: '1px solid oklch(0.62 calc(0.11 * var(--tint-c)) var(--tint-h) / 0.3)',
        color: 'rgba(255,255,255,0.92)',
        fontSize: Math.round(size * 0.4),
        fontWeight: 700,
        textShadow: '0 1px 3px rgba(0,0,0,0.35)',
      }}
    >
      {showImg ? (
        <img
          src={src as string}
          alt=""
          onError={() => setFailed(true)}
          style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
        />
      ) : (
        <span style={{ opacity: 0.9 }}>{letter}</span>
      )}
    </div>
  );
}
